import { hasExactly, isEditorName, type Fields } from "./protocol-shared.ts";

/**
 * The server's `publish` message (ADR-086's amendment of 2026-10-13, decision 1): what every editor of a world
 * is told while one builder publishes it and when the publish ends. It is presence's kind, not an operation:
 * it never enters the room's reducer, a ring or a journal, and moves no `LIVE_OPS_VERSION`. A tab reads it with
 * exactly its fields, each of its type and within its bounds, and skips a message whose `kind` it does not know.
 */

/**
 * The route's four phases, in the order a publish runs them.
 */
export const PUBLISH_PHASES = ["compile", "assets", "world", "pointer"] as const;

/**
 * One phase of `PUBLISH_PHASES`.
 */
export type PublishPhase = (typeof PUBLISH_PHASES)[number];

/**
 * The closed list of reasons a publish did not go through, mapped by the route from the place that threw and
 * never from a message.
 */
export const PUBLISH_REASONS = ["unsaved", "missing", "compile", "checks", "sheets", "upload", "unrecorded", "other"] as const;

/**
 * One reason of `PUBLISH_REASONS`.
 */
export type PublishReason = (typeof PUBLISH_REASONS)[number];

/**
 * The most a phase's count may total.
 */
export const PUBLISH_TOTAL_MAX = 100_000;

/**
 * The most milliseconds a publish's elapsed time may name: one day.
 */
export const PUBLISH_ELAPSED_MAX = 86_400_000;

/**
 * The most milliseconds the time left may name: ten minutes.
 */
export const PUBLISH_LEFT_MAX = 600_000;

/**
 * The longest `kind` a `publish` message may name, in code units, and a type name a reader skips.
 */
export const PUBLISH_KIND_MAX = 32;

const VERSION_LABEL = /^[A-Za-z0-9][A-Za-z0-9._-]{0,63}$/;
const KIND_NAME = /^[a-z]{1,32}$/;

/**
 * A publish under way: who runs it, the step and its count, the length of time it has run and the estimate of
 * what is left, both lengths and neither an instant, and whether it has run well past what was remembered.
 */
export interface PublishRunning {
  readonly t: "publish";
  readonly kind: "publish";
  readonly state: "running";
  readonly by: string;
  readonly phase: PublishPhase;
  readonly done: number;
  readonly total: number;
  readonly elapsed: number;
  readonly left: number | null;
  readonly late: boolean;
}

/**
 * A publish that is over: who ran it, how it ended, the version it gave and, for a failure, the reason, which is
 * null exactly when the outcome is `published`.
 */
export interface PublishEnded {
  readonly t: "publish";
  readonly kind: "publish";
  readonly state: "ended";
  readonly by: string;
  readonly outcome: "published" | "failed";
  readonly version: string | null;
  readonly reason: PublishReason | null;
}

/**
 * A well formed frame this reader does not know: a `publish` message of another kind, or a message of another
 * type. `of` is the kind or the type it named.
 */
export interface SkippedMessage {
  readonly t: "skipped";
  readonly of: string;
}

/**
 * Whether a value is a whole number from zero to a limit.
 *
 * @param value The value.
 * @param max The largest allowed.
 * @returns True for a safe integer from 0 to `max`.
 */
function isWhole(value: unknown, max: number): value is number {
  return typeof value === "number" && Number.isSafeInteger(value) && value >= 0 && value <= max;
}

/**
 * Whether a text is a name a reader skips: 1 to 32 lowercase letters.
 *
 * @param value The value.
 * @returns True for such a string.
 */
export function isSkippableName(value: unknown): value is string {
  return typeof value === "string" && KIND_NAME.test(value);
}

/**
 * Reads a `publish` message's fields.
 *
 * @param fields The fields of a frame whose `t` is `publish`.
 * @returns The message; a skipped message for a well formed `kind` this reader does not know; null when the
 *   fields are not exactly one of the two shapes.
 */
export function publishOf(fields: Fields): PublishRunning | PublishEnded | SkippedMessage | null {
  const kind = fields["kind"];
  if (kind !== "publish") {
    return isSkippableName(kind) ? { t: "skipped", of: kind } : null;
  }
  const { by, state } = fields;
  if (!isEditorName(by)) {
    return null;
  }
  if (state === "running") {
    const { phase, done, total, elapsed, left, late } = fields;
    const known = PUBLISH_PHASES.find((candidate) => candidate === phase);
    const valid = hasExactly(fields, ["t", "kind", "state", "by", "phase", "done", "total", "elapsed", "left", "late"]) && known !== undefined && isWhole(total, PUBLISH_TOTAL_MAX) && isWhole(done, PUBLISH_TOTAL_MAX) && done <= total && isWhole(elapsed, PUBLISH_ELAPSED_MAX) && (left === null || isWhole(left, PUBLISH_LEFT_MAX)) && typeof late === "boolean";
    return valid ? { t: "publish", kind: "publish", state: "running", by, phase: known, done, total, elapsed, left, late } : null;
  }
  if (state === "ended") {
    const { outcome, version, reason } = fields;
    const known = reason === null ? null : (PUBLISH_REASONS.find((candidate) => candidate === reason) ?? undefined);
    const shaped = hasExactly(fields, ["t", "kind", "state", "by", "outcome", "version", "reason"]) && (outcome === "published" || outcome === "failed") && (version === null || (typeof version === "string" && VERSION_LABEL.test(version))) && known !== undefined;
    return shaped && (known === null) === (outcome === "published") ? { t: "publish", kind: "publish", state: "ended", by, outcome, version, reason: known } : null;
  }
  return null;
}
